"use client";

import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";

import {
  ensureDPoPBoundSession,
  setSessionContext,
  subscribeToSessionContext,
} from "@/lib/dpop-client";
import { clearAllOfflineData } from "@/lib/pwa/offline-store";

// Types matching Auth.js Core
export interface User {
  id: string;
  name?: string | null;
  email?: string | null;
  image?: string | null;
  address?: string;
  vaultIds?: number[];
  activeVaultId?: number;
  isWalletConnected?: boolean;
  isEmbeddedWallet?: boolean;
  isPreapproved?: boolean;
  isVaultMember?: boolean;
  dpopKeyPair?: string;
}

export interface Session {
  user: User;
  fexrApiAccessToken?: string;
  fexrApiRefreshToken?: string;
  hasDpopKey?: boolean;
  dpopKeyPair?: string;
  requiresWalletConnection?: boolean;
  apiAuthenticationDegraded?: boolean;
  expires: string;
}

interface SessionContextType {
  data: Session | null;
  status: "loading" | "authenticated" | "unauthenticated";
  update: () => Promise<void>;
}

const SessionContext = createContext<SessionContextType>({
  data: null,
  status: "loading",
  update: async () => {},
});

// Every mounted provider awaits the same request. Unlike the previous boolean
// throttle, this cannot strand a newly mounted provider in "loading".
let globalSessionRequest: Promise<Session | null> | null = null;

function mergeStoredVault(session: Session): Session {
  const userId = session.user.id;
  const storedVaultId =
    localStorage.getItem(`activeVaultId_${userId}`) ||
    localStorage.getItem("activeVaultId");
  if (!storedVaultId) return session;

  const activeVaultId = Number.parseInt(storedVaultId, 10);
  if (!Number.isInteger(activeVaultId)) return session;

  return {
    ...session,
    user: { ...session.user, activeVaultId },
  };
}

async function requestSession(): Promise<Session | null> {
  if (globalSessionRequest) return globalSessionRequest;

  globalSessionRequest = (async () => {
    const response = await fetch("/api/auth/session", {
      method: "GET",
      credentials: "include",
      cache: "no-store",
      headers: {
        "Content-Type": "application/json",
        "Cache-Control": "no-cache, no-store, must-revalidate",
        Pragma: "no-cache",
      },
    });

    if (!response.ok) {
      throw new Error(`Session request failed (${response.status})`);
    }

    const text = await response.text();
    if (!text.trim()) return null;

    const sessionData = JSON.parse(text) as Session | null;
    if (!sessionData?.user?.id) return null;

    let securedSession = sessionData;
    try {
      securedSession = await ensureDPoPBoundSession(sessionData);
    } catch (error) {
      // The Auth.js cookie is still a valid login. A temporary IndexedDB or
      // DPoP binding failure must degrade API access, not erase identity and
      // masquerade as a logout.
      console.error(
        "[AUTH CLIENT] DPoP session preparation failed:",
        error instanceof Error ? error.message : error,
      );
      securedSession = { ...sessionData, apiAuthenticationDegraded: true };
    }

    return mergeStoredVault(securedSession);
  })().finally(() => {
    globalSessionRequest = null;
  });

  return globalSessionRequest;
}

// Session Provider using Auth.js Core endpoints
export function SessionProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [status, setStatus] = useState<
    "loading" | "authenticated" | "unauthenticated"
  >("loading");
  const sessionRef = useRef<Session | null>(null);
  const retryTimerRef = useRef<number | null>(null);
  const mountedRef = useRef(false);

  const applySession = useCallback((nextSession: Session | null) => {
    if (!mountedRef.current) return;
    sessionRef.current = nextSession;
    setSession(nextSession);
    setStatus(nextSession ? "authenticated" : "unauthenticated");
  }, []);

  const fetchSession = useCallback(async () => {
    if (typeof window === "undefined") return;

    try {
      const nextSession = await requestSession();
      applySession(nextSession);
      setSessionContext(nextSession);
      if (retryTimerRef.current !== null) {
        window.clearTimeout(retryTimerRef.current);
        retryTimerRef.current = null;
      }
    } catch (error) {
      console.error(
        "[AUTH CLIENT] Session temporarily unavailable:",
        error instanceof Error ? error.message : error,
      );

      // Preserve a known-good identity during network/5xx/429 failures. On
      // first load, keep the honest "checking" state and retry rather than
      // claiming the user signed out.
      if (!sessionRef.current && mountedRef.current) setStatus("loading");
      if (mountedRef.current && retryTimerRef.current === null) {
        retryTimerRef.current = window.setTimeout(() => {
          retryTimerRef.current = null;
          void globalSessionUpdate?.();
        }, 3000);
      }
    }
  }, [applySession]);

  // Handle initial hydration and session fetching
  useEffect(() => {
    mountedRef.current = true;
    // Register the session update function globally for signOut
    setGlobalSessionUpdate(fetchSession);
    const unsubscribe = subscribeToSessionContext((nextSession) => {
      applySession(nextSession as Session | null);
    });

    void fetchSession();
    return () => {
      mountedRef.current = false;
      unsubscribe();
      if (retryTimerRef.current !== null) {
        window.clearTimeout(retryTimerRef.current);
        retryTimerRef.current = null;
      }
    };
  }, [applySession, fetchSession]);

  // Listen for localStorage changes for activeVaultId
  useEffect(() => {
    if (typeof window === "undefined" || !session?.user?.id) return;

    const handleStorageChange = (e: StorageEvent) => {
      if (
        e.key === `activeVaultId_${session.user.id}` ||
        e.key === "activeVaultId"
      ) {
        console.log(
          "[AUTH CLIENT] localStorage change detected, updating session",
        );
        void fetchSession();
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, [fetchSession, session?.user?.id]);

  const update = useCallback(async () => {
    await fetchSession();
  }, [fetchSession]);

  // Minimal logging - only for critical errors in development

  return (
    <SessionContext.Provider value={{ data: session, status, update }}>
      {children}
    </SessionContext.Provider>
  );
}

// Custom hook
export function useSession() {
  const context = useContext(SessionContext);
  if (!context) {
    throw new Error("useSession must be used within a SessionProvider");
  }
  return context;
}

// Create global function references
let globalSessionUpdate: (() => Promise<void>) | null = null;
let globalDPoPClearSession: (() => void) | null = null;

export function setGlobalSessionUpdate(updateFn: () => Promise<void>) {
  globalSessionUpdate = updateFn;
}

export function resetSessionThrottle() {
  globalSessionRequest = null;
}

export function setGlobalDPoPClearSession(clearFn: () => void) {
  globalDPoPClearSession = clearFn;
}

// SIMPLE LOGOUT - Immediate redirect
export async function signOut(options?: {
  redirect?: boolean;
  callbackUrl?: string;
}) {
  const { redirect = true, callbackUrl = "/login" } = options || {};

  // STEP 1: IMMEDIATE CLIENT CLEANUP
  if (typeof window !== "undefined") {
    // Send queued journey events (and auth.signed_out) before storage goes;
    // clearing it also rotates the anonymous analytics id.
    await import("@/lib/analytics")
      .then((m) => m.flushBeforeSignOut())
      .catch(() => {});
    navigator.serviceWorker?.controller?.postMessage({
      type: "PWA_CLEAR_PRIVATE_CACHES",
    });
    await clearAllOfflineData();
    localStorage.clear();
    sessionStorage.clear();

    // Clear auth cookies immediately
    const authCookies = [
      "authjs.session-token",
      "authjs.csrf-token",
      "next-auth.session-token",
      "next-auth.csrf-token",
      "__Secure-authjs.session-token",
      "__Secure-next-auth.session-token",
      "__Host-authjs.session-token",
    ];

    authCookies.forEach((cookieName) => {
      document.cookie = `${cookieName}=; Path=/; Expires=Thu, 01 Jan 1970 00:00:00 GMT`;
    });
  }

  // STEP 2: Clear DPoP session immediately
  if (globalDPoPClearSession) {
    console.log("[LOGOUT] Clearing DPoP session");
    globalDPoPClearSession();
  }

  // STEP 3: Server cleanup FIRST - must complete to clear session
  try {
    // Call our emergency logout endpoint first
    await fetch("/api/logout", {
      method: "POST",
      headers: { "Cache-Control": "no-cache" },
      credentials: "include",
    });
    console.log("[LOGOUT] Emergency logout endpoint called");

    // Then call Auth.js signout to properly clear the session
    const csrfResponse = await fetch("/api/auth/csrf", {
      credentials: "include",
      cache: "no-cache",
      headers: { "Cache-Control": "no-cache" },
    });

    if (csrfResponse.ok) {
      const csrfData = await csrfResponse.json();
      const formData = new URLSearchParams();
      formData.append("csrfToken", csrfData.csrfToken);

      await fetch("/api/auth/signout", {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          "Cache-Control": "no-cache, no-store, must-revalidate",
          Pragma: "no-cache",
        },
        body: formData.toString(),
        credentials: "include",
      });
      console.log("[LOGOUT] Auth.js signout completed");
    }
  } catch (error) {
    console.log("[LOGOUT] Server cleanup failed:", error);
  }

  // STEP 4: Update session state after server cleanup
  if (globalSessionUpdate) {
    console.log("[LOGOUT] Updating session state");
    try {
      await globalSessionUpdate();
    } catch (e) {
      console.log("[LOGOUT] Session update failed, continuing");
    }
  }

  // STEP 5: FINAL REDIRECT after everything is cleaned up
  if (redirect && typeof window !== "undefined") {
    console.log("[LOGOUT] Redirecting to:", callbackUrl);
    window.location.replace(callbackUrl);
  }

  return { ok: true };
}

// Sign In function using Auth.js Core
export async function signIn(
  provider: string,
  options?: {
    redirect?: boolean;
    callbackUrl?: string;
    [key: string]: any;
  },
) {
  const {
    redirect = true,
    callbackUrl = "/dashboard",
    ...credentials
  } = options || {};

  // Development logging only for debugging auth issues
  if (process.env.NODE_ENV === "development") {
    console.log("[AUTH CLIENT] Starting signIn:", provider);
  }

  try {
    // Get CSRF token
    const csrfResponse = await fetch("/api/auth/csrf", {
      credentials: "include",
    });

    if (!csrfResponse.ok) {
      throw new Error(`CSRF request failed: ${csrfResponse.status}`);
    }

    const csrfData = await csrfResponse.json();

    // Prepare form data (Auth.js expects form data format)
    const formData = new URLSearchParams();
    formData.append("csrfToken", csrfData.csrfToken);
    formData.append("callbackUrl", callbackUrl);

    // Add provider-specific data
    if (
      provider === "credentials" ||
      provider === "email-link" ||
      provider === "enterprise"
    ) {
      Object.entries(credentials).forEach(([key, value]) => {
        if (value !== undefined) {
          const valueToSend =
            typeof value === "string" ? value : JSON.stringify(value);
          formData.append(key, valueToSend);
        }
      });
    }

    const requestBody = formData.toString();
    const contentType = "application/x-www-form-urlencoded";

    // Use Auth.js Core signin endpoint - NO trailing slash to avoid redirect
    const isCredentialsProvider =
      provider === "credentials" ||
      provider === "email-link" ||
      provider === "enterprise";
    const endpoint = isCredentialsProvider
      ? `/api/auth/callback/${provider}`
      : `/api/auth/signin/${provider}`;

    let response;
    try {
      response = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": contentType,
          // OAuth providers: tell Auth.js to return the authorization URL as JSON
          // instead of a 302 redirect, which fetch cannot follow cross-origin.
          ...(!isCredentialsProvider ? { "X-Auth-Return-Redirect": "1" } : {}),
        },
        body: requestBody,
        credentials: "include",
        redirect: "follow",
      });
    } catch (fetchError) {
      console.error("[AUTH CLIENT] ❌ Fetch error:", fetchError);
      throw new Error(
        `Network request failed: ${fetchError instanceof Error ? fetchError.message : String(fetchError)}`,
      );
    }

    // OAuth providers: Auth.js returns { url: '...' } JSON pointing to the provider
    if (!isCredentialsProvider && response.ok) {
      const data = await response.json().catch(() => null);
      if (data?.url) {
        return { ok: true, url: data.url };
      }
      return {
        ok: false,
        error: "OAuth sign-in failed: no redirect URL returned",
      };
    }

    // Check if we were redirected (authentication happened)
    if (response.redirected) {
      // A redirect to /login WITH ?error= means auth failed (wrong credentials, SIWE failure, etc.)
      // A redirect to /login WITHOUT ?error= is an edge case where auth succeeded but redirect logic sent to login
      if (response.url.includes("/login")) {
        try {
          const loginUrl = new URL(response.url);
          if (loginUrl.searchParams.has("error")) {
            return {
              ok: false,
              error: `Authentication failed: ${loginUrl.searchParams.get("error")}`,
            };
          }
        } catch {
          /* malformed URL — fall through */
        }
        return { ok: true, url: callbackUrl };
      }

      // Auth succeeded — now safe to refresh session
      if (globalSessionUpdate) {
        await globalSessionUpdate();

        // Safari-specific: Force additional session refresh after delay
        const isSafari = /^((?!chrome|android).)*safari/i.test(
          navigator.userAgent,
        );
        if (isSafari) {
          setTimeout(async () => {
            if (globalSessionUpdate) {
              console.log(
                "[AUTH CLIENT] Safari: Force refresh session after login",
              );
              await globalSessionUpdate();
            }
          }, 1000);
        }
      }

      return { ok: true, url: response.url };
    }

    // Check if response is ok (non-redirected success)
    if (response.ok) {
      // Update session immediately after successful authentication
      if (globalSessionUpdate) {
        await globalSessionUpdate();

        // Safari-specific: Force additional session refresh after delay
        const isSafari = /^((?!chrome|android).)*safari/i.test(
          navigator.userAgent,
        );
        if (isSafari) {
          setTimeout(async () => {
            if (globalSessionUpdate) {
              console.log(
                "[AUTH CLIENT] Safari: Force refresh session after non-redirected login",
              );
              await globalSessionUpdate();
            }
          }, 1000);
        }
      }

      // Since there's no redirect, use the callbackUrl
      return { ok: true, url: callbackUrl };
    } else {
      const errorText = await response.text().catch(() => "Unknown error");
      if (process.env.NODE_ENV === "development") {
        console.log("[AUTH CLIENT] Signin failed:", response.status, errorText);
      }
      return {
        ok: false,
        error:
          errorText || `Authentication failed with status ${response.status}`,
      };
    }
  } catch (error) {
    console.error("[AUTH CLIENT] Sign in error:", error);
    return {
      ok: false,
      error: error instanceof Error ? error.message : "Unknown error",
    };
  }
}

// Get Session function (for non-hook usage)
export async function getSession(): Promise<Session | null> {
  try {
    const response = await fetch("/api/auth/session", {
      credentials: "include",
    });

    if (response.ok) {
      const text = await response.text();
      if (text.trim()) {
        const sessionData = JSON.parse(text);
        if (sessionData && sessionData.user) {
          // Check localStorage for activeVaultId and merge it into session
          const userId = sessionData.user.id;
          const userSpecificVaultId =
            typeof window !== "undefined"
              ? localStorage.getItem(`activeVaultId_${userId}`)
              : null;
          const globalVaultId =
            typeof window !== "undefined"
              ? localStorage.getItem("activeVaultId")
              : null;

          if (userSpecificVaultId) {
            sessionData.user.activeVaultId = parseInt(userSpecificVaultId, 10);
            console.log(
              "[getSession] Updated activeVaultId from localStorage (user-specific):",
              sessionData.user.activeVaultId,
            );
          } else if (globalVaultId) {
            sessionData.user.activeVaultId = parseInt(globalVaultId, 10);
            console.log(
              "[getSession] Updated activeVaultId from localStorage (global):",
              sessionData.user.activeVaultId,
            );
          }

          return sessionData;
        }
        return null;
      }
    }

    return null;
  } catch (error) {
    console.error("Failed to get session:", error);
    return null;
  }
}
